import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { WithData, Nullable, UserWithToken } from './api.types';

// =============================================================================
// TYPES
// =============================================================================

// Bank account for transfer payment method
export interface SellerBankAccount {
  id: string;
  bankName: string;
  bankType: 'bank' | 'fintech';
  accountType: 'checking' | 'savings';
  cbuOrCvu: string;
  alias: string | null;
}

// Transfer payment method configuration
export interface SellerTransferConfig {
  accounts: SellerBankAccount[];
}

// MercadoPago payment method configuration
export interface SellerMercadoPagoConfig {
  cvu: string | null;
  alias: string | null;
  contact: string | null;
}

export interface SellerPaymentMethod {
  id: string;
  label: string;
  enabled: boolean;
  details: string | null;
  transferConfig: SellerTransferConfig | null;
  mercadoPagoConfig: SellerMercadoPagoConfig | null;
}

export interface SellerShippingMethod {
  id: string;
  label: string;
  enabled: boolean;
  price: number | null;
  description: string | null;
}

export interface SellerScheduleDay {
  enabled: boolean;
  openTime: string | null;
  closeTime: string | null;
}

export interface SellerWeeklySchedule {
  monday: SellerScheduleDay | null;
  tuesday: SellerScheduleDay | null;
  wednesday: SellerScheduleDay | null;
  thursday: SellerScheduleDay | null;
  friday: SellerScheduleDay | null;
  saturday: SellerScheduleDay | null;
  sunday: SellerScheduleDay | null;
}

export interface SellerPickupLocation {
  id: string;
  type: 'custom' | 'system';
  // Custom location fields
  name: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  zipCode: string | null;
  googleMapsUrl: string | null;
  latitude: string | null;
  longitude: string | null;
  enabled: boolean;
  // Schedule (structured)
  weeklySchedule: SellerWeeklySchedule | null;
  scheduleNotes: string | null;
  // System location reference
  locationId: number | null;
  location: {
    id: number;
    name: string;
    address: string;
    city: string;
    state: { id: number; name: string };
  } | null;
}

export interface SellerContactSettings {
  publicEmail: boolean;
  publicWhatsapp: boolean;
  whatsappNumber: string | null;
}

export interface SellerSettings {
  configured: boolean;
  paymentMethods: SellerPaymentMethod[];
  shippingMethods: SellerShippingMethod[];
  pickupLocations: SellerPickupLocation[];
  contactSettings: SellerContactSettings;
}

// Input types

export interface SellerBankAccountInput {
  id?: string | null;
  bankName: string;
  bankType: 'bank' | 'fintech';
  accountType: 'checking' | 'savings';
  cbuOrCvu: string;
  alias?: string | null;
}

export interface SellerTransferConfigInput {
  accounts: SellerBankAccountInput[];
}

export interface SellerMercadoPagoConfigInput {
  cvu?: string | null;
  alias?: string | null;
  contact?: string | null;
}

export interface SellerPaymentMethodInput {
  id: string;
  label: string;
  enabled: boolean;
  details?: string | null;
  transferConfig?: SellerTransferConfigInput | null;
  mercadoPagoConfig?: SellerMercadoPagoConfigInput | null;
}

export interface SellerShippingMethodInput {
  id: string;
  label: string;
  enabled: boolean;
  price?: number | null;
  description?: string | null;
}

export interface SellerScheduleDayInput {
  enabled: boolean;
  openTime?: string | null;
  closeTime?: string | null;
}

export interface SellerWeeklyScheduleInput {
  monday?: SellerScheduleDayInput | null;
  tuesday?: SellerScheduleDayInput | null;
  wednesday?: SellerScheduleDayInput | null;
  thursday?: SellerScheduleDayInput | null;
  friday?: SellerScheduleDayInput | null;
  saturday?: SellerScheduleDayInput | null;
  sunday?: SellerScheduleDayInput | null;
}

export interface SellerPickupLocationInput {
  id?: string | null;
  type: 'custom' | 'system';
  // Custom location fields
  name?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  zipCode?: string | null;
  googleMapsUrl?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  enabled: boolean;
  // Schedule (structured)
  weeklySchedule?: SellerWeeklyScheduleInput | null;
  scheduleNotes?: string | null;
  // System location reference
  locationId?: number | null;
}

export interface SellerContactSettingsInput {
  publicEmail: boolean;
  publicWhatsapp: boolean;
  whatsappNumber?: string | null;
}

export interface UpdateSellerSettingsInput {
  paymentMethods: SellerPaymentMethodInput[];
  shippingMethods: SellerShippingMethodInput[];
  pickupLocations: SellerPickupLocationInput[];
  contactSettings?: SellerContactSettingsInput;
}

export interface QuickSellerSetupInput {
  paymentMethod: 'transfer' | 'mercadopago' | 'cash';
  bankName?: string;
  cbuOrCvu?: string;
  alias?: string;
  mercadoPagoAlias?: string;
  deliveryOption: 'shipping' | 'pickup';
  shippingMethod?: string;
  shippingPrice?: number;
}

// =============================================================================
// FRAGMENTS
// =============================================================================

const SELLER_SETTINGS_FRAGMENT = gql`
  fragment SellerSettingsFields on SellerSettings {
    configured
    paymentMethods {
      id
      label
      enabled
      details
      transferConfig {
        accounts {
          id
          bankName
          bankType
          accountType
          cbuOrCvu
          alias
        }
      }
      mercadoPagoConfig {
        cvu
        alias
        contact
      }
    }
    shippingMethods {
      id
      label
      enabled
      price
      description
    }
    pickupLocations {
      id
      type
      name
      address
      city
      province
      zipCode
      googleMapsUrl
      latitude
      longitude
      enabled
      weeklySchedule {
        monday { enabled openTime closeTime }
        tuesday { enabled openTime closeTime }
        wednesday { enabled openTime closeTime }
        thursday { enabled openTime closeTime }
        friday { enabled openTime closeTime }
        saturday { enabled openTime closeTime }
        sunday { enabled openTime closeTime }
      }
      scheduleNotes
      locationId
      location {
        id
        name
        address
        city
        state {
          id
          name
        }
      }
    }
    contactSettings {
      publicEmail
      publicWhatsapp
      whatsappNumber
    }
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

// Get current user's seller settings
export const getMySellerSettings = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<SellerSettings>>(
    gql`
      ${SELLER_SETTINGS_FRAGMENT}
      query mySellerSettings {
        data: mySellerSettings {
          ...SellerSettingsFields
        }
      }
    `
  );

// Get seller settings by user ID (for marketplace)
export const getSellerSettings = async (user: Nullable<UserWithToken>, userId: number) =>
  getGraphQLQueryClient(user).request<WithData<SellerSettings | null>>(
    gql`
      ${SELLER_SETTINGS_FRAGMENT}
      query sellerSettings($userId: Int!) {
        data: sellerSettings(userId: $userId) {
          ...SellerSettingsFields
        }
      }
    `,
    { userId }
  );

// =============================================================================
// MUTATIONS
// =============================================================================

// Update seller settings
export const updateSellerSettings = async (user: Nullable<UserWithToken>, input: UpdateSellerSettingsInput) =>
  getGraphQLQueryClient(user).request<WithData<SellerSettings>>(
    gql`
      ${SELLER_SETTINGS_FRAGMENT}
      mutation updateSellerSettings($input: UpdateSellerSettingsInput!) {
        data: updateSellerSettings(input: $input) {
          ...SellerSettingsFields
        }
      }
    `,
    { input }
  );

// Quick setup for first-time sellers
export const quickSellerSetup = async (user: Nullable<UserWithToken>, input: QuickSellerSetupInput) =>
  getGraphQLQueryClient(user).request<WithData<SellerSettings>>(
    gql`
      ${SELLER_SETTINGS_FRAGMENT}
      mutation quickSellerSetup($input: QuickSellerSetupInput!) {
        data: quickSellerSetup(input: $input) {
          ...SellerSettingsFields
        }
      }
    `,
    { input }
  );
